import { useEffect, useMemo, useRef } from "react";
import { useVendorDetails } from "../../../features/bills/presentation/hooks/useVendorDetails";

/**
 * Resolves vendor GST fields from document JSON, fetching vendor by vendor_id when missing.
 * Bills only — no-op when vendorId is empty.
 */
export function useResolvedVendorGst(
  json: Record<string, unknown> | null | undefined,
  enabled = true,
): {
  resolvedGstTreatment: string;
  resolvedGstCategory: string;
} {
  const vendorId = String(json?.vendor_id ?? "").trim();
  const jsonTreatment = String(json?.gst_treatment ?? "").trim();
  const jsonCategory = String(json?.gst_category ?? "").trim();
  const needsFetch =
    enabled &&
    !!vendorId &&
    (!jsonTreatment || !jsonCategory);

  const { vendor, fetchVendor } = useVendorDetails();
  const vendorHydratedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!needsFetch) {
      return;
    }
    if (vendorHydratedRef.current === vendorId) {
      return;
    }
    vendorHydratedRef.current = vendorId;
    fetchVendor(vendorId).catch(() => {
      vendorHydratedRef.current = null;
    });
  }, [needsFetch, vendorId, fetchVendor]);

  const resolvedGstTreatment = useMemo(
    () => String(vendor?.gst_treatment ?? jsonTreatment),
    [vendor?.gst_treatment, jsonTreatment],
  );
  const resolvedGstCategory = useMemo(
    () => String(vendor?.gst_category ?? jsonCategory),
    [vendor?.gst_category, jsonCategory],
  );

  return { resolvedGstTreatment, resolvedGstCategory };
}
